import React, { useState, useEffect, useCallback, useMemo } from "react";

// ─── Palette (matches iOS DistributionValuePalette) ───

const VALUE_PALETTE = [
  "#2563eb", "#b45309", "#7c3aed", "#0d9488", "#be123c",
  "#ca8a04", "#0369a1", "#4d7c0f", "#9333ea", "#64748b",
];

function stablePaletteIndex(normalized, count) {
  let hash = 5381;
  for (let i = 0; i < normalized.length; i++) {
    hash = (hash << 5) + hash + normalized.charCodeAt(i);
    hash = hash >>> 0;
  }
  return hash % count;
}

function colorFor(value, serverOverrides, allValues) {
  if (serverOverrides && serverOverrides[value]) {return serverOverrides[value];}
  if (allValues && allValues.length > 0) {
    const idx = allValues.indexOf(value);
    if (idx >= 0) {return VALUE_PALETTE[idx % VALUE_PALETTE.length];}
  }
  return VALUE_PALETTE[stablePaletteIndex(value.trim().toLowerCase(), VALUE_PALETTE.length)];
}

// ─── Shape Definitions (exact from Swift) ───

const SHAPES = {
  simpleGable: {
    viewBox: { width: 400, height: 280 },
    outline: [
      [200, 20], [380, 140], [380, 260], [20, 260], [20, 140],
    ],
    sections: [
      { id: "sg-centre", label: "Whole Roof", x: 200, y: 140, isCenter: true },
      { id: "sg-front-left", label: "Front Left", x: 60, y: 200 },
      { id: "sg-front-right", label: "Front Right", x: 340, y: 200 },
      { id: "sg-rear-left", label: "Rear Left", x: 60, y: 100 },
      { id: "sg-rear-right", label: "Rear Right", x: 340, y: 100 },
      { id: "sg-ridge", label: "Ridge", x: 200, y: 20 },
    ],
  },
  lShaped: {
    viewBox: { width: 400, height: 320 },
    outline: [
      [60, 40], [260, 40], [260, 120], [340, 120], [340, 300], [140, 300], [140, 220], [60, 220],
    ],
    sections: [
      { id: "ls-centre", label: "Whole Roof", x: 200, y: 160, isCenter: true },
      { id: "ls-front-left", label: "Front Left", x: 60, y: 220 },
      { id: "ls-front-right", label: "Front Right", x: 340, y: 220 },
      { id: "ls-rear-left", label: "Rear Left", x: 60, y: 90 },
      { id: "ls-rear-right", label: "Rear Right", x: 260, y: 90 },
      { id: "ls-inner-front", label: "Inner Front", x: 260, y: 220 },
      { id: "ls-inner-rear", label: "Inner Rear", x: 140, y: 210 },
      { id: "ls-ridge", label: "Ridge", x: 200, y: 45 },
    ],
  },
  hip: {
    viewBox: { width: 400, height: 280 },
    outline: [
      [120, 30], [280, 30], [380, 120], [380, 260], [20, 260], [20, 120],
    ],
    sections: [
      { id: "hr-centre", label: "Whole Roof", x: 200, y: 140, isCenter: true },
      { id: "hr-front-left", label: "Front Left", x: 100, y: 210 },
      { id: "hr-front-right", label: "Front Right", x: 300, y: 210 },
      { id: "hr-rear-left", label: "Rear Left", x: 100, y: 100 },
      { id: "hr-rear-right", label: "Rear Right", x: 300, y: 100 },
      { id: "hr-ridge-left", label: "Left Ridge", x: 120, y: 30 },
      { id: "hr-ridge-right", label: "Right Ridge", x: 280, y: 30 },
    ],
  },
  crossGable: {
    viewBox: { width: 400, height: 320 },
    outline: [
      [70, 40], [330, 40], [330, 150], [260, 150], [260, 290], [140, 290], [140, 150], [70, 150],
    ],
    sections: [
      { id: "cg-centre", label: "Whole Roof", x: 200, y: 95, isCenter: true },
      { id: "cg-rear-left", label: "Rear Left", x: 120, y: 90 },
      { id: "cg-rear-right", label: "Rear Right", x: 280, y: 90 },
      { id: "cg-front-left", label: "Front Left", x: 170, y: 230 },
      { id: "cg-front-right", label: "Front Right", x: 230, y: 230 },
    ],
  },
  flat: {
    viewBox: { width: 400, height: 280 },
    outline: [
      [40, 40], [360, 40], [360, 240], [40, 240],
    ],
    sections: [
      { id: "fl-centre", label: "Whole Roof", x: 200, y: 140, isCenter: true },
      { id: "fl-front-left", label: "Front Left", x: 130, y: 180 },
      { id: "fl-front-right", label: "Front Right", x: 270, y: 180 },
      { id: "fl-rear-left", label: "Rear Left", x: 130, y: 80 },
      { id: "fl-rear-right", label: "Rear Right", x: 270, y: 80 },
    ],
  },
};

const SHAPE_KEYS = ["simpleGable", "lShaped", "hip", "crossGable", "flat"];
const SHAPE_LABELS = { simpleGable: "Simple Gable", lShaped: "L-Shaped", hip: "Hip", crossGable: "Cross Gable", flat: "Flat" };

// ─── Helpers ───

function parseAnswer(json) {
  if (!json) {return { version: 2, items: [], notes: "" };}
  try {
    const parsed = JSON.parse(json);
    return { version: 2, items: parsed.items || [], notes: parsed.notes || "" };
  } catch {
    return { version: 2, items: [], notes: "" };
  }
}

function serializeAnswer(items, notes) {
  const clean = { version: 2, items: items.map((it) => ({ value: it.value, percent: it.percent })) };
  if (notes) {clean.notes = notes;}
  return JSON.stringify(clean);
}

function equalizePercents(count) {
  const base = Math.floor(100 / count);
  const rem = 100 - base * count;
  return Array.from({ length: count }, (_, i) => base + (i === count - 1 ? rem : 0));
}

function redistribute(items, addedValue) {
  const values = items.map((it) => it.value).concat([addedValue]);
  const percents = equalizePercents(values.length);
  return values.map((v, i) => ({ value: v, percent: percents[i] }));
}

function removeAndRedistribute(items, removeValue) {
  const filtered = items.filter((it) => it.value !== removeValue);
  if (filtered.length === 0) {return [];}
  const percents = equalizePercents(filtered.length);
  return filtered.map((it, i) => ({ ...it, percent: percents[i] }));
}

function stepPercent(items, idx, delta) {
  const copy = items.map((it) => ({ ...it }));
  copy[idx].percent = Math.max(0, Math.min(100, copy[idx].percent + delta));
  const edited = copy[idx].percent;
  const othersTarget = 100 - edited;
  const otherIndices = copy.map((_, i) => i).filter((i) => i !== idx);
  const currentSum = otherIndices.reduce((s, i) => s + copy[i].percent, 0);
  if (currentSum > 0) {
    const scale = othersTarget / currentSum;
    let allocated = 0;
    for (let j = 0; j < otherIndices.length - 1; j++) {
      const i = otherIndices[j];
      const raw = Math.round(copy[i].percent * scale);
      copy[i].percent = Math.max(0, raw);
      allocated += copy[i].percent;
    }
    copy[otherIndices[otherIndices.length - 1]].percent = Math.max(0, othersTarget - allocated);
  }
  return copy;
}

function totalPercent(items) {
  return items.reduce((s, it) => s + it.percent, 0);
}

function pathString(points) {
  if (!points.length) {return "";}
  return `M${points[0][0]},${points[0][1]} ` + points.slice(1).map((p) => `L${p[0]},${p[1]}`).join(" ") + " Z";
}

// ─── Floating panel positioning helper ───

function panelStyle(section, viewBox) {
  const pctX = section.x / viewBox.width * 100;
  const pctY = section.y / viewBox.height * 100;
  const isRight = section.x > viewBox.width * 0.55;
  const isBottom = section.y > viewBox.height * 0.55;
  return {
    position: "absolute",
    left: `${pctX}%`,
    top: `${pctY}%`,
    transform: `translate(${isRight ? "-108%" : "12%"}, ${isBottom ? "-108%" : "12%"})`,
    zIndex: 20,
    width: "240px",
    maxWidth: "calc(100% - 24px)",
  };
}

// ─── Main Component ───

export default function FormRoofGeometryQuestion({ targetInputId, question, initialAnswer }) {
  const allValues = question.values || [];
  const valueColors = useMemo(() => {
    const map = {};
    if (question.valueColors) {
      for (const vc of question.valueColors) {
        map[vc.key] = vc.colorHex;
      }
    }
    return map;
  }, [question.valueColors]);

  const [shapeKey, setShapeKey] = useState("simpleGable");
  const [sectionSelections, setSectionSelections] = useState({});
  const [notes, setNotes] = useState("");
  const [modalSection, setModalSection] = useState(null);
  const [otherModal, setOtherModal] = useState(false);
  const [otherDesc, setOtherDesc] = useState("");

  const shape = SHAPES[shapeKey];

  // Load initial answer
  useEffect(() => {
    const parsed = parseAnswer(initialAnswer);
    if (parsed.items.length > 0) {
      const reconstructed = {};
      for (const item of parsed.items) {
        const parts = item.value.split("::");
        if (parts.length === 2) {
          const [sectionId, typeName] = parts;
          if (!reconstructed[sectionId]) {reconstructed[sectionId] = [];}
          reconstructed[sectionId].push({ value: typeName, percent: item.percent });
        }
      }
      const savedIds = Object.keys(reconstructed);
      for (const sk of SHAPE_KEYS) {
        const shapeIds = SHAPES[sk].sections.map((s) => s.id);
        if (savedIds.some((id) => shapeIds.includes(id))) {
          setShapeKey(sk);
          break;
        }
      }
      setSectionSelections(reconstructed);
    }
    setNotes(parsed.notes || "");
  }, [initialAnswer]);

  // Serialize and write to hidden input
  useEffect(() => {
    const items = [];
    for (const [sectionId, geometryItems] of Object.entries(sectionSelections)) {
      for (const gi of geometryItems) {
        items.push({ value: `${sectionId}::${gi.value}`, percent: Math.max(0, gi.percent) });
      }
    }
    const input = document.getElementById(targetInputId);
    if (input) {
      input.value = serializeAnswer(items, question.allowNotes ? notes : "");
    }
  }, [sectionSelections, notes, targetInputId, question.allowNotes]);

  // Initialize hidden input on mount
  useEffect(() => {
    const input = document.getElementById(targetInputId);
    if (input && !input.value) {
      input.value = serializeAnswer([], "");
    }
  }, []);

  // Remap selections when shape changes (match by label)
  const handleShapeChange = useCallback((newKey) => {
    const newShape = SHAPES[newKey];
    const newSectionIds = new Set(newShape.sections.map((s) => s.id));
    const labelToNewId = {};
    for (const s of newShape.sections) {labelToNewId[s.label] = s.id;}

    setSectionSelections((prev) => {
      const remapped = {};
      for (const [oldId, items] of Object.entries(prev)) {
        if (!items || items.length === 0) {continue;}
        let oldLabel = null;
        for (const sk of SHAPE_KEYS) {
          const sec = SHAPES[sk].sections.find((s) => s.id === oldId);
          if (sec) { oldLabel = sec.label; break; }
        }
        if (newSectionIds.has(oldId)) {
          remapped[oldId] = items;
        } else if (oldLabel && labelToNewId[oldLabel]) {
          remapped[labelToNewId[oldLabel]] = items;
        }
      }
      return remapped;
    });
    setShapeKey(newKey);
  }, []);

  const classifiedCount = Object.values(sectionSelections).filter((arr) => arr && arr.length > 0).length;
  const totalSections = shape.sections.length;

  // Aggregated distribution by type
  const aggregated = useMemo(() => {
    const map = {};
    for (const [, items] of Object.entries(sectionSelections)) {
      if (!items || items.length === 0) {continue;}
      for (const it of items) {
        map[it.value] = (map[it.value] || 0) + it.percent;
      }
    }
    const total = Object.values(map).reduce((s, v) => s + v, 0);
    if (total === 0) {return [];}
    return Object.entries(map)
      .map(([type, pct]) => ({ type, percent: Math.round(pct / total * 100) }))
      .sort((a, b) => b.percent - a.percent);
  }, [sectionSelections]);

  const aggTotal = aggregated.reduce((s, it) => s + it.percent, 0);

  // Per-section cards
  const visibleSectionIds = useMemo(() => {
    const populated = Object.keys(sectionSelections).filter((id) => sectionSelections[id]?.length > 0);
    const centerId = shape.sections.find((s) => s.isCenter)?.id;
    const centerItems = sectionSelections[centerId];
    if (!centerId || !centerItems || centerItems.length === 0) {return populated;}
    const allMatchCenter = populated.every((id) => {
      if (id === centerId) {return true;}
      const items = sectionSelections[id];
      if (!items || items.length !== centerItems.length) {return false;}
      return items.every((it, i) => it.value === centerItems[i].value && it.percent === centerItems[i].percent);
    });
    if (allMatchCenter) {return [centerId];}
    return populated;
  }, [sectionSelections, shape]);

  const hasPartial = Object.values(sectionSelections).some((items) => items && items.length > 0 && totalPercent(items) < 100);

  return (
    <div className="tw-space-y-4">
      {/* Shape selector */}
      <div className="tw-flex tw-rounded-lg tw-overflow-hidden tw-border tw-border-gray-200" style={{ borderRadius: 10 }}>
        {SHAPE_KEYS.map((sk) => 
          <button
            key={sk}
            type="button"
            onClick={() => handleShapeChange(sk)}
            className={`tw-flex-1 tw-px-1 tw-py-2 tw-text-xs tw-font-semibold tw-transition ${
              shapeKey === sk ? "tw-bg-black tw-text-white" : "tw-bg-gray-100 tw-text-gray-500 hover:tw-bg-gray-200"
            }`}
          >
            {SHAPE_LABELS[sk]}
          </button>
        )}
      </div>

      {/* Diagram with inline floating panel */}
      <div className="tw-relative tw-rounded-xl tw-border tw-border-gray-200 tw-bg-gray-50" style={{ height: 280, overflow: "visible" }}>
        <svg
          viewBox={`0 0 ${shape.viewBox.width} ${shape.viewBox.height}`}
          className="tw-w-full tw-h-full"
          preserveAspectRatio="xMidYMid meet"
        >
          {/* Roof fill */}
          <path
            d={pathString(shape.outline)}
            fill={modalSection ? "#d1d5db" : "#e5e7eb"}
            stroke="#9ca3af"
            strokeWidth="1.5"
          />
          {/* Dashed overlay on fill */}
          <path
            d={pathString(shape.outline)}
            fill="none"
            stroke="#6b7280"
            strokeWidth="0.8"
            strokeDasharray="5 4"
            opacity="0.3"
          />
          {/* Hotspots */}
          {shape.sections.map((section) => {
            const items = sectionSelections[section.id] || [];
            const hasItems = items.length > 0;
            const primaryColor = hasItems ? colorFor(items[0].value, valueColors, allValues) : "#2563eb";
            const isRidge = section.label === "Ridge" || section.label.includes("Ridge");
            const showLabelBelow = isRidge || section.y < 60;
            const isActive = modalSection?.id === section.id;

            return (
              <g key={section.id}>
                {section.isCenter ? 
                  <g
                    className={`tw-cursor-pointer ${isActive ? "tw-opacity-50" : ""}`}
                    onClick={() => setModalSection(section)}
                  >
                    <circle
                      cx={section.x}
                      cy={section.y}
                      r={22}
                      fill={hasItems ? primaryColor : "white"}
                      stroke={primaryColor}
                      strokeWidth={2.5}
                      strokeDasharray={hasItems ? "" : "4 3"}
                    />
                    {hasItems && items.length === 1 && 
                      <text
                        x={section.x}
                        y={section.y + 3}
                        textAnchor="middle"
                        className="tw-text-[9px] tw-font-bold tw-fill-white tw-pointer-events-none"
                      >
                        {items[0].value}
                      </text>
                    }
                    {hasItems && items.length > 1 && 
                      <text
                        x={section.x}
                        y={section.y + 3}
                        textAnchor="middle"
                        className="tw-text-[11px] tw-font-bold tw-fill-white tw-pointer-events-none"
                      >
                        {items.length}
                      </text>
                    }
                  </g>
                  : 
                  <g
                    className={`tw-cursor-pointer ${isActive ? "tw-opacity-50" : ""}`}
                    onClick={() => setModalSection(section)}
                  >
                    <circle
                      cx={section.x}
                      cy={section.y}
                      r={hasItems ? 16 : 14}
                      fill={primaryColor}
                      stroke="white"
                      strokeWidth={2.5}
                    />
                    <circle
                      cx={section.x}
                      cy={section.y}
                      r={hasItems ? 14 : 12}
                      fill={hasItems ? primaryColor : `${primaryColor}26`}
                    />
                    {hasItems && 
                      <text
                        x={section.x}
                        y={section.y + 4}
                        textAnchor="middle"
                        className="tw-text-sm tw-font-bold tw-fill-white tw-pointer-events-none"
                      >
                        ✓
                      </text>
                    }
                    {hasItems && !showLabelBelow && 
                      <g>
                        <rect x={section.x - 30} y={section.y - 32} width={60} height={18} rx={4} fill={primaryColor} />
                        <text
                          x={section.x}
                          y={section.y - 20}
                          textAnchor="middle"
                          className="tw-text-[9px] tw-font-semibold tw-fill-white tw-pointer-events-none"
                        >
                          {items.length === 1 ? items[0].value : `${items.length} types`}
                        </text>
                      </g>
                    }
                  </g>
                }
                {!hasItems && 
                  <text
                    x={section.x}
                    y={section.y + (section.isCenter ? 32 : 22)}
                    textAnchor="middle"
                    className="tw-text-[9px] tw-font-semibold tw-fill-gray-500 tw-pointer-events-none"
                  >
                    {section.label}
                  </text>
                }
                {hasItems && showLabelBelow && !section.isCenter && 
                  <g>
                    <rect x={section.x - 30} y={section.y + 14} width={60} height={18} rx={4} fill={primaryColor} />
                    <text
                      x={section.x}
                      y={section.y + 26}
                      textAnchor="middle"
                      className="tw-text-[9px] tw-font-semibold tw-fill-white tw-pointer-events-none"
                    >
                      {items.length === 1 ? items[0].value : `${items.length} types`}
                    </text>
                  </g>
                }
              </g>
            );
          })}
        </svg>

        {/* Orientation hint */}
        <div className="tw-absolute tw-bottom-2 tw-left-2 tw-right-2 tw-flex tw-justify-between tw-pointer-events-none" style={{ zIndex: 5 }}>
          <span className="tw-text-[9px] tw-text-gray-500 tw-bg-white/85 tw-px-2 tw-py-1 tw-rounded">🧭 Front (mailbox)</span>
          <span className="tw-text-[9px] tw-text-gray-500 tw-bg-white/85 tw-px-2 tw-py-1 tw-rounded">Rear →</span>
        </div>

        {/* Inline floating panel */}
        {modalSection && 
          <>
            {/* Backdrop over diagram only */}
            <div
              className="tw-absolute tw-inset-0 tw-bg-black/20 tw-z-10"
              onClick={() => { setModalSection(null); setOtherModal(false); }}
            />
            {/* Floating card */}
            <div
              className="tw-absolute tw-z-20 tw-rounded-lg tw-border tw-border-gray-200 tw-bg-white tw-shadow-lg tw-p-3 tw-max-h-[260px] tw-overflow-y-auto"
              style={panelStyle(modalSection, shape.viewBox)}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="tw-flex tw-items-center tw-justify-between tw-mb-2">
                <div>
                  <h3 className="tw-text-sm tw-font-bold">{modalSection.label}</h3>
                  {(() => {
                    const items = sectionSelections[modalSection.id] || [];
                    if (items.length === 0) {return null;}
                    return (
                      <p className="tw-text-[10px] tw-text-gray-500">
                        {items.map((it) => `${it.value} ${it.percent}%`).join(", ")}
                      </p>
                    );
                  })()}
                </div>
                <button
                  type="button"
                  onClick={() => { setModalSection(null); setOtherModal(false); }}
                  className="tw-text-xs tw-font-semibold tw-text-blue-600 hover:tw-text-blue-800"
                >
                  Done
                </button>
              </div>

              {!otherModal ? 
                <SectionGeometrySheet
                  section={modalSection}
                  sectionSelections={sectionSelections}
                  setSectionSelections={setSectionSelections}
                  allValues={allValues}
                  valueColors={valueColors}
                  onClose={() => { setModalSection(null); setOtherModal(false); }}
                  onOpenOther={() => setOtherModal(true)}
                />
                : 
                <div className="tw-space-y-2">
                  <p className="tw-text-xs tw-font-medium tw-text-gray-500">Describe other roof type</p>
                  <input
                    type="text"
                    className="tw-w-full tw-rounded tw-border tw-border-gray-200 tw-px-2 tw-py-1 tw-text-xs"
                    placeholder="Describe the roof type…"
                    value={otherDesc}
                    onChange={(e) => setOtherDesc(e.target.value)}
                    autoFocus
                  />
                  <div className="tw-flex tw-gap-2">
                    <button
                      type="button"
                      onClick={() => { setOtherModal(false); setOtherDesc(""); }}
                      className="tw-flex-1 tw-py-1.5 tw-rounded tw-bg-gray-100 tw-text-xs tw-text-gray-700"
                    >
                      Back
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        if (!otherDesc.trim()) {return;}
                        const desc = otherDesc.trim();
                        setSectionSelections((prev) => {
                          const current = prev[modalSection.id] || [];
                          const next = { ...prev };
                          const newItems = redistribute(current, desc);
                          next[modalSection.id] = newItems;
                          if (modalSection.isCenter) {
                            for (const s of shape.sections) {
                              if (s.id !== modalSection.id) {next[s.id] = newItems.map((it) => ({ ...it }));}
                            }
                          }
                          return next;
                        });
                        setOtherModal(false);
                        setOtherDesc("");
                      }}
                      disabled={!otherDesc.trim()}
                      className={`tw-flex-1 tw-py-1.5 tw-rounded tw-text-xs ${otherDesc.trim() ? "tw-bg-black tw-text-white" : "tw-bg-gray-200 tw-text-gray-400"}`}
                    >
                      Add Other
                    </button>
                  </div>
                </div>
              }

              {(() => {
                const items = sectionSelections[modalSection.id] || [];
                if (items.length === 0) {return null;}
                return (
                  <button
                    type="button"
                    onClick={() => {
                      setSectionSelections((prev) => {
                        const next = { ...prev };
                        if (modalSection.isCenter) {
                          for (const s of shape.sections) {delete next[s.id];}
                        } else {
                          delete next[modalSection.id];
                        }
                        return next;
                      });
                      setModalSection(null);
                      setOtherModal(false);
                    }}
                    className="tw-mt-2 tw-w-full tw-py-1.5 tw-text-xs tw-font-medium tw-text-red-500 hover:tw-text-red-700"
                  >
                    Clear this section
                  </button>
                );
              })()}
            </div>
          </>
        }
      </div>

      {/* Progress row */}
      <div className="tw-flex tw-items-center tw-justify-between">
        <span className="tw-text-xs tw-text-gray-500">{classifiedCount} of {totalSections} sections classified</span>
        {classifiedCount > 0 && 
          <button
            type="button"
            onClick={() => setSectionSelections({})}
            className="tw-text-xs tw-font-medium tw-text-red-500 hover:tw-text-red-700"
          >
            Clear all
          </button>
        }
      </div>

      {/* Overall Distribution */}
      {aggregated.length > 0 && 
        <div className="tw-space-y-2">
          <p className="tw-text-xs tw-font-semibold tw-text-gray-500 tw-text-center">Distribution</p>
          <div className="tw-flex tw-h-8 tw-w-full tw-overflow-hidden tw-rounded-lg">
            {aggregated.map((item) => 
              <div
                key={item.type}
                className="tw-flex tw-items-center tw-justify-center tw-text-[10px] tw-font-semibold tw-text-white"
                style={{ width: `${item.percent}%`, backgroundColor: colorFor(item.type, valueColors, allValues), minWidth: 2 }}
              >
                {item.percent >= 15 ? item.type : ""}
              </div>
            )}
          </div>
          {aggTotal === 100 && classifiedCount === totalSections && 
            <p className="tw-text-xs tw-font-semibold tw-text-green-600 tw-text-center">
              ✓ {classifiedCount} of {totalSections} sections classified — 100%
            </p>
          }
          {hasPartial && 
            <p className="tw-text-xs tw-font-medium tw-text-amber-600 tw-text-center">Some sections need completion</p>
          }
        </div>
      }

      {/* Section Detail Cards */}
      {visibleSectionIds.map((sectionId) => {
        const section = shape.sections.find((s) => s.id === sectionId);
        if (!section) {return null;}
        const items = sectionSelections[sectionId] || [];
        return (
          <div key={sectionId} className="tw-rounded-lg tw-border tw-border-gray-200 tw-bg-white tw-p-3 tw-space-y-2">
            <div className="tw-flex tw-items-center tw-justify-between">
              <span className="tw-text-xs tw-font-semibold">{section.label}</span>
            </div>
            <div className="tw-flex tw-h-6 tw-w-full tw-overflow-hidden tw-rounded-md">
              {items.map((item) => 
                <div
                  key={item.value}
                  className="tw-flex tw-items-center tw-justify-center tw-text-[10px] tw-font-semibold tw-text-white"
                  style={{ width: `${item.percent}%`, backgroundColor: colorFor(item.value, valueColors, allValues), minWidth: 2 }}
                >
                  {item.percent >= 25 ? item.value : ""}
                </div>
              )}
            </div>
            {items.map((item) => 
              <div key={item.value} className="tw-flex tw-items-center tw-gap-2">
                <span className="tw-h-1.5 tw-w-1.5 tw-rounded-full tw-flex-shrink-0" style={{ backgroundColor: colorFor(item.value, valueColors, allValues) }} />
                <span className="tw-flex-1 tw-text-xs">{item.value}</span>
                <span className="tw-text-xs tw-font-medium tw-text-gray-500">{item.percent}%</span>
              </div>
            )}
          </div>
        );
      })}

      {/* Notes */}
      {question.allowNotes && 
        <div>
          <label className="tw-text-xs tw-font-medium tw-text-gray-500">Notes</label>
          <textarea
            className="tw-mt-1 tw-block tw-w-full tw-rounded-lg tw-border tw-border-gray-200 tw-bg-white tw-px-3 tw-py-2 tw-text-sm"
            rows={2}
            placeholder="Observations"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
          />
        </div>
      }
    </div>
  );
}

// ─── Section Geometry Sheet (inline floating panel content) ───

function SectionGeometrySheet({ section, sectionSelections, setSectionSelections, allValues, valueColors, onClose, onOpenOther }) {
  const items = sectionSelections[section.id] || [];
  const selectedSet = new Set(items.map((it) => it.value));
  const available = allValues.filter((v) => !selectedSet.has(v));
  const tp = totalPercent(items);

  const setItems = useCallback((newItems) => {
    setSectionSelections((prev) => {
      const next = { ...prev };
      next[section.id] = newItems;
      if (section.isCenter) {
        for (const sk of SHAPE_KEYS) {
          for (const s of SHAPES[sk].sections) {
            if (s.id !== section.id) {next[s.id] = newItems.map((it) => ({ ...it }));}
          }
        }
      }
      return next;
    });
  }, [section, setSectionSelections]);

  const addType = (value) => {
    if (value === "Other") {
      onOpenOther();
      return;
    }
    const newItems = redistribute(items, value);
    setItems(newItems);
    if (newItems.length === allValues.length) {onClose();}
  };

  const removeType = (value) => {
    const newItems = removeAndRedistribute(items, value);
    setItems(newItems);
  };

  const step = (idx, delta) => {
    const newItems = stepPercent(items, idx, delta);
    setItems(newItems);
  };

  return (
    <div className="tw-space-y-2">
      {items.length > 0 && 
        <div className="tw-space-y-1">
          <p className="tw-text-[10px] tw-font-medium tw-text-gray-500">Adjust percentages</p>
          {items.map((item, idx) => 
            <div key={item.value} className="tw-flex tw-items-center tw-gap-1.5">
              <span className="tw-h-1.5 tw-w-1.5 tw-rounded-full tw-flex-shrink-0" style={{ backgroundColor: colorFor(item.value, valueColors, allValues) }} />
              <span className="tw-flex-1 tw-text-[11px] tw-truncate">{item.value}</span>
              <div className="tw-flex tw-items-center tw-gap-0.5">
                <button
                  type="button"
                  onClick={() => step(idx, -5)}
                  className="tw-h-6 tw-w-6 tw-flex tw-items-center tw-justify-center tw-rounded tw-bg-gray-100 tw-text-gray-700 tw-text-xs hover:tw-bg-gray-200"
                >
                  −
                </button>
                <span className="tw-w-8 tw-text-center tw-text-[11px] tw-font-semibold">{item.percent}%</span>
                <button
                  type="button"
                  onClick={() => step(idx, 5)}
                  className="tw-h-6 tw-w-6 tw-flex tw-items-center tw-justify-center tw-rounded tw-bg-gray-100 tw-text-gray-700 tw-text-xs hover:tw-bg-gray-200"
                >
                  +
                </button>
              </div>
              <button
                type="button"
                onClick={() => removeType(item.value)}
                className="tw-h-6 tw-w-6 tw-flex tw-items-center tw-justify-center tw-text-gray-400 hover:tw-text-red-500"
              >
                ×
              </button>
            </div>
          )}
          <p className={`tw-text-[10px] tw-font-medium ${tp === 100 ? "tw-text-green-600" : "tw-text-amber-600"}`}>
            Total: {tp}%
          </p>
        </div>
      }

      {items.length > 0 && available.length > 0 && <hr className="tw-border-gray-100" />}

      {available.length > 0 && 
        <div className="tw-space-y-1">
          <p className="tw-text-[10px] tw-font-medium tw-text-gray-500">Add roof geometry type</p>
          <div className="tw-grid tw-grid-cols-2 tw-gap-1.5">
            {available.map((value) => 
              <button
                key={value}
                type="button"
                onClick={() => addType(value)}
                className="tw-flex tw-items-center tw-gap-1.5 tw-rounded tw-border tw-border-gray-200 tw-bg-white tw-px-2 tw-py-1.5 tw-text-[11px] tw-font-medium hover:tw-bg-gray-50"
              >
                <span className="tw-h-1.5 tw-w-1.5 tw-rounded-full tw-flex-shrink-0" style={{ backgroundColor: colorFor(value, valueColors, allValues) }} />
                {value}
              </button>
            )}
          </div>
        </div>
      }
    </div>
  );
}
